import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';

interface DevelopmentStatusFilterProps {
    value: string[];
    onChange: (value: string[]) => void;
    translate: (key: string, opts?: Record<string, unknown>) => string;
    statuses: readonly string[];
}

export function DevelopmentStatusFilter({ value, onChange, translate, statuses }: DevelopmentStatusFilterProps) {
    const selectRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (!open) {
            return;
        }
        const handleClose = (e: Event) => {
            const target = e.target as Element | Document;
            if (target instanceof Element && target.closest('.MuiMenu-paper')) {
                return;
            }
            setOpen(false);
        };
        window.addEventListener('scroll', handleClose, true);
        return () => window.removeEventListener('scroll', handleClose, true);
    }, [open]);

    const handleOpen = () => {
        if (selectRef.current) {
            setMenuWidth(selectRef.current.clientWidth);
        }
        setOpen(true);
    };

    const renderValue = (selected: string[]) => {
        if (selected.length === 0) {
            return <Typography sx={{ color: 'text.disabled' }}>{translate('devList.allStatuses')}</Typography>;
        }
        return <Typography>{translate('devList.statusSelectedCount', { count: selected.length })}</Typography>;
    };

    const handleChange = (event: SelectChangeEvent<string[]>) => {
        const selected = event.target.value;
        onChange(typeof selected === 'string' ? selected.split(',') : selected);
    };

    return (
        <FormControl size="small" sx={{ minWidth: 200 }} ref={selectRef}>
            <Select<string[]>
                multiple
                value={value}
                onChange={handleChange}
                renderValue={renderValue}
                displayEmpty
                open={open}
                onOpen={handleOpen}
                onClose={() => setOpen(false)}
                data-testid="status-filter-select"
                MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                        sx: { width: menuWidth }
                    }
                }}
            >
                {statuses.map((s) => (
                    <MenuItem key={s} value={s}>
                        <Checkbox checked={value.indexOf(s) > -1} />
                        <ListItemText primary={translate(`statusLabels.${s}`)} slotProps={{ primary: { sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } } }} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
